import { importLibrary, setOptions } from "@googlemaps/js-api-loader";
import { useCallback, useEffect, useRef, useState } from "react";
import { useStores } from "../../../store/stores.js";
import { parseAddressComponents } from "../utils/addressParser.js";
function toNormalisedFromPlace(components) {
    return components.map((component)=>({
            long: component.longText ?? "",
            short: component.shortText ?? "",
            types: component.types
        }));
}
function useAddressAutocomplete(form) {
    const { optionStore } = useStores();
    const googleMapsApiKey = optionStore.others?.googleMapsApiKey ?? "";
    const apiKeyConfigured = googleMapsApiKey.length > 0;
    const [enabled, setEnabled] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const optionsInitialisedRef = useRef(false);
    const mapsLoadedRef = useRef(false);
    const sessionTokenRef = useRef(null);
    // Keeps `PlacePrediction` objects returned by `fetchAutocompleteSuggestions`
    // so `onSelect` can call `prediction.toPlace()` and keep the Place Details
    // call inside the same billable session as the suggestions.
    const predictionCacheRef = useRef(new Map());
    const searchDebounceRef = useRef(null);
    /**
     * Loads the Places library via the functional `@googlemaps/js-api-loader`
     * API (v2 replaced the `Loader.load()` class pattern with `setOptions()`
     * + `importLibrary()`). Idempotent: subsequent calls return immediately.
     * Places API (New) exposes `AutocompleteSuggestion` statically, so no
     * service-instance construction is needed — we only bootstrap the
     * autocomplete session token.
     */ const loadGoogleMaps = useCallback(async ()=>{
        if (!googleMapsApiKey || mapsLoadedRef.current) {
            return mapsLoadedRef.current;
        }
        try {
            if (!optionsInitialisedRef.current) {
                setOptions({
                    key: googleMapsApiKey
                });
                optionsInitialisedRef.current = true;
            }
            const places = await importLibrary("places");
            sessionTokenRef.current = new places.AutocompleteSessionToken();
            mapsLoadedRef.current = true;
            setEnabled(true);
            return true;
        } catch (error) {
            console.warn("[checkout-v2:address] loadGoogleMaps: error during library load", error);
            return false;
        }
    }, [
        googleMapsApiKey
    ]);
    /**
     * Auto-load Maps on mount if the buyer has RCB consent for "google-maps".
     * The `consentApi.consent()` Promise resolves immediately when consent is
     * pre-given (stored from a prior visit or another page in this session)
     * AND also awaits the `OPT_IN_EVENT` — so if the buyer gives consent
     * directly on the checkout page (e.g. by accepting the RCB banner here),
     * this effect wires autocomplete up as soon as that consent lands,
     * without requiring a page reload. Rejection (explicit opt-out) is
     * ignored silently; the feature just stays dormant.
     */ useEffect(()=>{
        if (!apiKeyConfigured || mapsLoadedRef.current) {
            return undefined;
        }
        let cancelled = false;
        const { consentApi } = window;
        if (!consentApi) {
            // No RCB on this site — nothing to gate, load directly.
            void loadGoogleMaps();
            return undefined;
        }
        consentApi.consent("google-maps").then(()=>{
            if (cancelled) return;
            void loadGoogleMaps();
        }, ()=>{
        // Opt-out — stay dormant, buyer can type manually.
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        apiKeyConfigured,
        loadGoogleMaps
    ]);
    /**
     * Typing handler — debounced, silent until the library is loaded. Fires
     * only when the buyer has previously consented to Maps (typically via
     * RCB elsewhere on the site). No implicit consent prompt on typing.
     */ const onSearch = useCallback((value)=>{
        const token = sessionTokenRef.current;
        if (!mapsLoadedRef.current || !token || !value || value.length < 3) {
            setSuggestions([]);
            return;
        }
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(async ()=>{
            try {
                const request = {
                    input: value,
                    sessionToken: token,
                    includedPrimaryTypes: [
                        "street_address",
                        "route"
                    ]
                };
                const { suggestions: results } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
                predictionCacheRef.current.clear();
                const mapped = [];
                for (const suggestion of results){
                    const { placePrediction: prediction } = suggestion;
                    if (!prediction) continue;
                    const { placeId } = prediction;
                    const label = prediction.text?.text ?? "";
                    if (!placeId || !label) continue;
                    predictionCacheRef.current.set(placeId, prediction);
                    mapped.push({
                        label,
                        value: label,
                        placeId
                    });
                }
                setSuggestions(mapped);
            } catch (error) {
                console.warn("[checkout-v2:address] fetchAutocompleteSuggestions error", error);
                setSuggestions([]);
            }
        }, 300);
    }, []);
    /**
     * Selection handler — resolves the cached `PlacePrediction` to a Place
     * and fetches address components (billed within the same session as the
     * suggestions). Rotates the session token afterwards so the next typing
     * cycle is a fresh billable session.
     */ const onSelect = useCallback(async (_value, option)=>{
        if (!option.placeId) return;
        const prediction = predictionCacheRef.current.get(option.placeId);
        if (!prediction) return;
        try {
            const place = prediction.toPlace();
            await place.fetchFields({
                fields: [
                    "addressComponents"
                ]
            });
            if (!place.addressComponents) return;
            const parsed = parseAddressComponents(toNormalisedFromPlace(place.addressComponents));
            form.setFieldsValue({
                street: parsed.street,
                city: parsed.city,
                zip: parsed.zip,
                country: parsed.countryCode
            });
            setSuggestions([]);
            sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
            predictionCacheRef.current.clear();
        } catch (error) {
            console.warn("[checkout-v2:address] place.fetchFields error", error);
        }
    }, [
        form
    ]);
    return {
        enabled,
        suggestions,
        onSearch,
        onSelect,
        apiKeyConfigured
    };
}
export { useAddressAutocomplete };
